import { styled } from "@linaria/react";
import { type FC, useEffect, useRef, useState } from "react";
import { IconX } from "@/icons/IconX.tsx";
import { theme } from "@/themes/theme.ts";
import { useChatStore } from "@/stores/useChatStore.ts";
import { createRoot } from "react-dom/client";

const WINDOW_WIDTH = 32 * 16;
const WINDOW_PADDING = 8;
const MIN_WINDOW_WIDTH = 16 * 16;
const MAX_WINDOW_WIDTH = 48 * 16;

const Window = styled.div`
  position: fixed;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: ${theme.brandBorderRadius};
  corner-shape: squircle;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  width: ${WINDOW_WIDTH}px;
  cursor: grab;
  user-select: none;

  backface-visibility: hidden;
  perspective: 1000px;
  transform: translateZ(1px);

  &:active {
    cursor: grabbing;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.1rem;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 99rem;
  cursor: pointer;
  color: #64748b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    background: white;
    color: #334155;
    rotate: -15deg;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  padding: ${WINDOW_PADDING}px;
  user-select: none;
`;

const FloatingImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  pointer-events: none;
  user-select: none;
`;

const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 1rem;
  height: 1rem;
  cursor: nwse-resize;
  z-index: 2;
`;

interface FloatingImageWindowProps {
  src: string;
  alt: string;
  initialX: number;
  initialY: number;
  onClose: () => void;
  ownedChat?: number;
}

let HighestZIndex = 1000;

const FloatingWindow: FC<FloatingImageWindowProps> = ({
  src,
  alt,
  initialX,
  initialY,
  onClose,
  ownedChat,
}) => {
  const { activeChat } = useChatStore();
  const [position, setPosition] = useState({
    x: initialX - WINDOW_WIDTH / 2,
    y: initialY - WINDOW_WIDTH * 0.375,
  });
  const [dragging, setDragging] = useState(true);
  const [resizing, setResizing] = useState(false);
  const [windowWidth, setWindowWidth] = useState(WINDOW_WIDTH);
  const [aspectRatio, setAspectRatio] = useState(4 / 3);
  const offsetRef = useRef({ x: WINDOW_WIDTH / 2, y: WINDOW_WIDTH * 0.375 });
  const resizeStartRef = useRef({
    width: WINDOW_WIDTH,
    pointerX: 0,
    pointerY: 0,
  });

  useEffect(() => {
    if (!dragging || resizing) return;

    const onMove = (e: PointerEvent) => {
      setPosition({
        x: e.clientX - offsetRef.current.x,
        y: e.clientY - offsetRef.current.y,
      });
    };

    const onUp = (e: PointerEvent) => {
      setDragging(false);

      const target = e.target as HTMLElement;

      target.style.cursor = "pointer";
    };

    const onDown = (e: PointerEvent) => {
      setDragging(true);

      HighestZIndex += 1;

      const target = e.target as HTMLElement;
      target.style.cursor = "grabbing";

      if (target.parentElement) target.parentElement.style.zIndex = String(HighestZIndex);
    };

    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, resizing]);

  useEffect(() => {
    if (!resizing) return;

    const onMove = (e: PointerEvent) => {
      const deltaX = e.clientX - resizeStartRef.current.pointerX;
      const deltaY = e.clientY - resizeStartRef.current.pointerY;
      const nextWidth = Math.max(
        MIN_WINDOW_WIDTH,
        Math.min(
          MAX_WINDOW_WIDTH,
          Math.round(
            Math.max(
              resizeStartRef.current.width + deltaX,
              resizeStartRef.current.width + deltaY * aspectRatio,
            ),
          ),
        ),
      );

      setWindowWidth(nextWidth);
    };

    const onUp = () => setResizing(false);

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [resizing, aspectRatio]);

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    if ((e.target as HTMLElement).closest("[data-resize-handle='true']")) return;
    e.preventDefault();
    offsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    setDragging(true);
  };

  const onResizePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStartRef.current = {
      width: windowWidth,
      pointerX: e.clientX,
      pointerY: e.clientY,
    };
    setResizing(true);
  };

  // TODO: Unload if the session is 'completed'
  const visible = activeChat.unwrapOrElse(() => 0) === ownedChat;

  return (
    <Window
      style={{
        left: position.x,
        top: position.y,
        width: windowWidth,
        visibility: visible ? "visible" : "hidden",
        display: visible ? "block" : "none",
      }}
      onPointerDown={onPointerDown}
      className={"glass"}
    >
      <ImageWrapper>
        <FloatingImage
          src={src}
          alt={alt}
          onLoad={(e) => {
            const image = e.currentTarget;
            if (image.naturalWidth > 0 && image.naturalHeight > 0) {
              setAspectRatio(image.naturalWidth / image.naturalHeight);
            }
          }}
        />
        <CloseButton type="button" onClick={onClose} aria-label="Return image to chat">
          <IconX width="18" height="18" color="currentColor" />
        </CloseButton>
        <ResizeHandle
          data-resize-handle="true"
          onPointerDown={onResizePointerDown}
          aria-label="Resize image window"
        />
      </ImageWrapper>
    </Window>
  );
};

export const FloatingImageWindow: FC<FloatingImageWindowProps> = ({
  src,
  alt,
  initialX,
  initialY,
  onClose,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (rootRef.current) return;

    const container = document.createElement("div");
    document.body.appendChild(container);
    rootRef.current = container;

    const root = createRoot(container);
    root.render(
      <FloatingWindow
        src={src}
        alt={alt}
        initialX={initialX}
        initialY={initialY}
        ownedChat={useChatStore.getState().activeChat.unwrapOrElse(() => 0)}
        onClose={() => {
          root.unmount();
          document.body.removeChild(container);
          onClose();
        }}
      />,
    );
    // Note to any body reading this:
    // We do NOT want to unload here, the image must live for longer than this component
    return () => {};
  }, [alt, initialX, initialY, onClose, src]);

  return <></>;
};
